import {
  ROLE_AUTHENTICATED,
  ROLE_JOURNEY_MANAGER_BETA_USER_ADVANCED_EDIT,
  ROLE_POLLY_RESEARCH_MANAGER,
  ROLE_POLLY_CRAWL_SOURCE_MANAGER,
} from "./roles";

// Long-term home: the API. These checks only shape the UI (hide/disable controls); they
// are not a security boundary, since anyone can call the API directly. The API must own
// ROLE_PERMISSIONS and enforce every permission, then hand the portal the user's effective
// permissions. At that point the portal keeps only the permission name constants and
// drops ROLE_PERMISSIONS / permissionsForGroups.

// Named permissions the portal checks. UI gates on these, never on a Cognito group:
// a role grants permissions through ROLE_PERMISSIONS below.
//
// Format: `resource:action` for a whole API action, `resource.field:action` for one
// field of it. Names are chosen to match the API actions that will enforce them.
export const PERMISSION_JOURNEYS_VIEW = "journeys:view";
export const PERMISSION_JOURNEYS_CREATE = "journeys:create";
export const PERMISSION_JOURNEYS_UPDATE = "journeys:update";
export const PERMISSION_JOURNEYS_DELETE = "journeys:delete";
export const PERMISSION_TOUCHPOINTS_CREATE = "touchpoints:create";
export const PERMISSION_TOUCHPOINTS_DELETE = "touchpoints:delete";
export const PERMISSION_TOUCHPOINTS_UPDATE = "touchpoints:update";
export const PERMISSION_TOUCHPOINTS_CONTENT_UPDATE = "touchpoints.content:update";
export const PERMISSION_TOUCHPOINTS_NAME_UPDATE = "touchpoints.name:update";
export const PERMISSION_TOUCHPOINTS_SCHEDULE_UPDATE = "touchpoints.schedule:update";
export const PERMISSION_TOUCHPOINTS_ORDER_UPDATE = "touchpoints.order:update";
export const PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE = "touchpoints.attributes:update";
export const PERMISSION_TOUCHPOINTS_LABELS_UPDATE = "touchpoints.labels:update";
export const PERMISSION_POLLY_RESEARCH_MANAGE = "polly.research:manage";
export const PERMISSION_POLLY_CRAWL_SOURCES_MANAGE = "polly.crawl_sources:manage";

// Rollups: holding a key permission grants every permission it lists (one level, one
// direction: holding all the listed permissions does not grant the key). The API must
// expand rollups the same way.
export const PERMISSION_ROLLUPS = {
  [PERMISSION_TOUCHPOINTS_UPDATE]: [
    PERMISSION_TOUCHPOINTS_CONTENT_UPDATE,
    PERMISSION_TOUCHPOINTS_NAME_UPDATE,
    PERMISSION_TOUCHPOINTS_SCHEDULE_UPDATE,
    PERMISSION_TOUCHPOINTS_ORDER_UPDATE,
    PERMISSION_TOUCHPOINTS_ATTRIBUTES_UPDATE,
    PERMISSION_TOUCHPOINTS_LABELS_UPDATE,
  ],
};

// Role → permissions it grants. Keys are ROLE_AUTHENTICATED or Cognito group names.
// A user's permissions are the union of ROLE_AUTHENTICATED and every group they belong to.
// Belongs in the API long term (see top of file).
export const ROLE_PERMISSIONS = {
  [ROLE_AUTHENTICATED]: [
    PERMISSION_JOURNEYS_VIEW,
    PERMISSION_TOUCHPOINTS_CONTENT_UPDATE,
  ],
  [ROLE_JOURNEY_MANAGER_BETA_USER_ADVANCED_EDIT]: [
    PERMISSION_JOURNEYS_CREATE,
    PERMISSION_JOURNEYS_UPDATE,
    PERMISSION_JOURNEYS_DELETE,
    PERMISSION_TOUCHPOINTS_CREATE,
    PERMISSION_TOUCHPOINTS_DELETE,
    PERMISSION_TOUCHPOINTS_UPDATE,
  ],
  [ROLE_POLLY_RESEARCH_MANAGER]: [
    PERMISSION_POLLY_RESEARCH_MANAGE,
  ],
  [ROLE_POLLY_CRAWL_SOURCE_MANAGER]: [
    PERMISSION_POLLY_CRAWL_SOURCES_MANAGE,
  ],
};

// Every permission check resolves the user's full permission set, so it is cached per
// groups array. The store replaces that array (never mutates it) on sign-in or account
// switch, so a new array means a fresh resolve. WeakMap so entries are dropped with it.
const permissionsCache = new WeakMap();

// Permissions held by a user with the given Cognito groups. No groups (not signed
// in) holds nothing, not even ROLE_AUTHENTICATED. Rollups are expanded here, so checks
// only ever look up a single permission. Replaced by API-supplied permissions long term
// (see top of file).
export const permissionsForGroups = (groups) => {
  if (!groups) return new Set();
  const cached = permissionsCache.get(groups);
  if (cached) return cached;
  const granted = [ROLE_AUTHENTICATED, ...groups].flatMap((role) => ROLE_PERMISSIONS[role] || []);
  const held = new Set(granted.flatMap((permission) => [permission, ...(PERMISSION_ROLLUPS[permission] || [])]));
  permissionsCache.set(groups, held);
  return held;
};

// Any-of: true when the groups grant at least one of the given permission(s).
export const groupsHavePermission = (groups, permissionOrPermissions) => {
  const held = permissionsForGroups(groups);
  return [].concat(permissionOrPermissions).some((p) => held.has(p));
};
